import type { Indexes } from "./indexes";
import { create as createIndexes } from "./indexes";
import { create as createTransaction } from "./transactions";

type Store = {
  parameters?: IDBObjectStoreParameters;
  indexes?: Indexes;
};

export type Stores = {
  [key: string]: Store;
};

export type Operations<D extends Stores> = {
  [K in keyof D]: {
    all: <T>(key?: IDBValidKey, index?: keyof D[K]["indexes"]) => Promise<IDBRequest<T[]>>;
    get: <T>(key: IDBValidKey, index?: keyof D[K]["indexes"]) => Promise<IDBRequest<T>>;
    add: <T>(value: T) => Promise<IDBRequest<IDBValidKey>>;
    addMany: <T>(values: T[], key?: IDBValidKey) => Promise<IDBRequest<IDBValidKey>[]>;
    put: <T>(value: T, key?: IDBValidKey) => Promise<IDBRequest<IDBValidKey>>;
    putMany: <T>(values: T[], key?: IDBValidKey) => Promise<IDBRequest<IDBValidKey>[]>;
    delete: (key: IDBValidKey) => Promise<IDBRequest<undefined>>;
    deleteMany: (keys: IDBValidKey) => Promise<IDBRequest<undefined>[]>;
    clear: () => Promise<IDBRequest<undefined>>;
  };
};

/**
 * @description
 * Migrate the database to the latest version based on the stores definitions.
 * Must be called in the IDBDatabase onupgradeneeded event handler.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IDBDatabase/onupgradeneeded
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IDBDatabase/createObjectStore
 */
export const migrateObjectStores = (db: IDBDatabase, stores: Stores) => {
  for (const [name, store] of Object.entries(stores)) {
    if (db.objectStoreNames.contains(name)) {
      db.deleteObjectStore(name);
    }
    createIndexes(db.createObjectStore(name, store.parameters), store.indexes ?? {});
  }
};

/**
 * @description
 * Store getAll operation.
 * Can be used with or without an IDBIndex, if defined in store definition.
 * If used with an IDBIndex, the index must be a valid index for the store.
 * Otherwise, the index must be a valid index for the store.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/getAll
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IDBIndex/getAll
 */
const all =
  <D extends Store>(definition: D) =>
  <T>(
    store: IDBObjectStore,
    key?: IDBValidKey,
    index?: keyof (typeof definition)["indexes"],
  ): IDBRequest<T[]> =>
    store.indexNames.contains(index as string)
      ? store.index(index as string).getAll(key)
      : store.getAll(key);

/**
 * @description
 * Store get operation.
 * Can be used with or without an IDBIndex, if defined in store definition.
 * If used with an IDBIndex, the key must be a valid key for the index.
 * Otherwise, the key must be a valid key for the store.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/get
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IDBIndex/get
 */
const get =
  <D extends Store>(definition: D) =>
  <T>(
    store: IDBObjectStore,
    key: IDBValidKey,
    index: keyof (typeof definition)["indexes"],
  ): IDBRequest<T> =>
    store.indexNames.contains(index as string)
      ? store.index(index as string).get(key)
      : store.get(key);

/**
 * @description
 * Store add operation.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/add
 */
const add = <T>(store: IDBObjectStore, value: T): IDBRequest<IDBValidKey> => store.add(value);
/**
 * @description
 * Bulk store put operation.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/put
 */
const addMany = <T>(store: IDBObjectStore, values: T[]): IDBRequest<IDBValidKey>[] =>
  values.map(value => store.add(value));

/**
 * @description
 * Store put operation.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/put
 */
const put = <T>(store: IDBObjectStore, value: T, key?: IDBValidKey): IDBRequest<IDBValidKey> =>
  store.put(value, key);

/**
 * @description
 * Bulk store put operation.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/put
 */
const putMany = <T>(store: IDBObjectStore, values: T[]): IDBRequest<IDBValidKey>[] =>
  values.map(value => store.put(value));

/**
 * @description
 * Store delete operation.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/delete
 */
const del = (store: IDBObjectStore, key: IDBValidKey): IDBRequest<undefined> => store.delete(key);
/**
 * @description
 * Bulk store put operation.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/put
 */
const delMany = (store: IDBObjectStore, keys: IDBValidKey[]): IDBRequest<undefined>[] =>
  keys.map(key => store.delete(key));

/**
 * @description
 * Store clear operation.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/clear
 */
const clear = (store: IDBObjectStore): IDBRequest<undefined> => store.clear();
/**
 * @description
 * Create accessors for a store.
 * Accessors create corresponding transactions (asynchronous) based on its operation.
 * Must be called in the IDBDatabase onsuccess event handler.
 */
export const defineObjectStoreOperations = <D extends Store>(
  db: IDBDatabase,
  name: string,
  definition: D,
) => ({
  all: createTransaction(db, name, "readonly", all(definition)),
  get: createTransaction(db, name, "readonly", get(definition)),
  add: createTransaction(db, name, "readwrite", add),
  addMany: createTransaction(db, name, "readwrite", addMany),
  put: createTransaction(db, name, "readwrite", put),
  putMany: createTransaction(db, name, "readwrite", putMany),
  delete: createTransaction(db, name, "readwrite", del),
  deleteMany: createTransaction(db, name, "readwrite", delMany),
  clear: createTransaction(db, name, "readwrite", clear),
});

/**
 * @description
 * Create accessors for all stores based on their definition.
 * Must be called in the IDBDatabase onsuccess event handler.
 */
export const createObjectStoreOperations = <D extends Stores>(
  db: IDBDatabase,
  definitions: D,
): Operations<D> =>
  Object.entries(definitions).reduce(
    (acc, [name, definition]) => ({
      ...acc,
      [name]: defineObjectStoreOperations(db, name, definition),
    }),
    {} as Operations<D>,
  );

/**
 * @description
 * Create store state management for all stores based on their definition.
 * This utility function is based on React's useSyncExternalStore hook and
 * is meant to be used to brige the gap between browser API and React App state.
 */
export const createObjectStoreState = <Op extends Operations<Stores>[string], Snapshot, Model>(
  operations: Op,
  snapshot: (operations: Op) => Promise<Snapshot>,
) => {
  const state: { value: Snapshot | null } = { value: null };

  const listeners = new Set<() => void>();

  const notify = () => {
    for (const l of listeners) l();
  };

  const update = async <T>(x?: T): Promise<T | void> => {
    await snapshot(operations)
      .then(s => (state.value = s))
      .then(notify);
    return x;
  };

  void update();

  return {
    snapshot: () => state.value,
    subscribe: (listener: () => void) => (
      listeners.add(listener), () => listeners.delete(listener)
    ),
    all: operations?.all<Model>,
    get: operations?.get<Model>,

    add: (...args: Parameters<typeof operations.add<Model>>) =>
      operations
        ?.add(...args)
        .then(update)
        .catch(err => {
          throw err;
        }),

    addMany: (...args: Parameters<typeof operations.addMany<Model>>) =>
      operations
        ?.addMany(...args)
        .then(update)
        .catch(err => {
          throw err;
        }),

    put: (...args: Parameters<typeof operations.put<Model>>) =>
      operations
        ?.put(...args)
        .then(update)
        .catch(err => {
          throw err;
        }),

    putMany: (...args: Parameters<typeof operations.putMany<Model>>) =>
      operations
        ?.putMany(...args)
        .then(update)
        .catch(err => {
          throw err;
        }),

    delete: (...args: Parameters<typeof operations.delete>) =>
      operations
        ?.delete(...args)
        .then(update)
        .catch(err => {
          throw err;
        }),

    deleteMany: (...args: Parameters<typeof operations.delete>) =>
      operations
        ?.deleteMany(...args)
        .then(update)
        .catch(err => {
          throw err;
        }),

    clear: () =>
      operations
        ?.clear()
        .then(update)
        .catch(err => {
          throw err;
        }),
  };
};
